import React, { useCallback, useMemo, useState } from 'react'
import { Button, Modal, Spinner } from 'react-bootstrap'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import { IsGranted, ROLE, UserModel } from '../../../auth'
import { canUser, matchingCapabilities } from '../../core/_models'
import { createVirtualMerchantPayment, declineMatchingPayoutTicket } from '../../core/_requests'
import { buildVirtualPaymentErrorText, virtualPaymentErrorFromException } from '../../core/virtualPaymentErrors'

/**
 * Права на действия над матчинговой выплатой. Список активных выплат и карточка выплаты считают
 * их одной функцией: расходящиеся права у одной и той же операции замечают уже в проде.
 */
export const usePayoutTicketPermissions = (currentUser?: UserModel) =>
  useMemo(() => {
    const canAccess = canUser(currentUser, matchingCapabilities.canViewActivePayments)
    const canManageTickets = canAccess && canUser(currentUser, matchingCapabilities.canEditTickets)
    const canCreateVirtualPayment =
      canManageTickets &&
      !currentUser?.roles.includes(ROLE.ROLE_MATCHING_OPERATOR) &&
      !currentUser?.roles.includes(ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR) &&
      !currentUser?.roles.includes(ROLE.ROLE_MERCHANT_MANAGER)
    // Переотправка КБ — отдельное право от canManageTickets; список ролей — в matchingCapabilities.
    const canResend = IsGranted(matchingCapabilities.canResendCallback, currentUser)

    return { canAccess, canManageTickets, canCreateVirtualPayment, canResend }
  }, [currentUser])

type Options = {
  /** Право на операции над выплатой. Без него запросы не уходят. */
  enabled: boolean
  /** Вызывается после выполненной операции: список перечитывается, карточка перезагружается. */
  onDone: () => void
}

const AppSwal = withReactContent(Swal)

/** Что с виртуальным платежом по выплате на этом экране: кнопка строки читает это состояние. */
export type VirtualPaymentState = 'idle' | 'submitting' | 'created'

/**
 * Виртуальный платёж и отмена матчинговой выплаты — с окнами подтверждения.
 *
 * У операции один путь: список активных выплат и карточка выплаты зовут этот хук. `modals`
 * рендерится один раз на экран.
 *
 * Пока запрос идёт, подтверждение и кнопка строки погашены; после успеха кнопка строки скрыта до
 * перечитывания списка. Вторая вкладка и гонка всё равно упираются в бэкенд — он отвечает кодом,
 * и сотрудник видит причину.
 */
export const usePayoutTicketActions = ({ enabled, onDone }: Options) => {
  const [completeToken, setCompleteToken] = useState<string>()
  const [cancelToken, setCancelToken] = useState<string>()
  const [submittingToken, setSubmittingToken] = useState<string>()
  const [createdTokens, setCreatedTokens] = useState<ReadonlySet<string>>(() => new Set())
  // Выплата открытого окна на паузе: окно предупреждает об этом перед подтверждением.
  const [confirmPaused, setConfirmPaused] = useState(false)

  const complete = useCallback(
    async (ticketToken?: string) => {
      if (!enabled || !ticketToken || submittingToken) return
      setSubmittingToken(ticketToken)
      try {
        const response = await createVirtualMerchantPayment(ticketToken)

        if (response.data?.success) {
          setCreatedTokens((prev) => new Set(prev).add(ticketToken))
          onDone()
        } else {
          AppSwal.fire('Виртуальный платёж не создан', buildVirtualPaymentErrorText(response.data), 'warning')
        }
      } catch (error) {
        // Модалка в catch заменяет общий алерт перехватчика в том же тике.
        AppSwal.fire('Виртуальный платёж не создан', virtualPaymentErrorFromException(error), 'warning')
      } finally {
        setSubmittingToken(undefined)
        setCompleteToken(undefined)
      }
    },
    [enabled, onDone, submittingToken]
  )

  const openComplete = useCallback(
    (token: string, paused = false) => {
      if (submittingToken || createdTokens.has(token)) return
      setConfirmPaused(paused)
      setCompleteToken(token)
    },
    [submittingToken, createdTokens]
  )

  const openCancel = useCallback((token: string, paused = false) => {
    setConfirmPaused(paused)
    setCancelToken(token)
  }, [])

  const virtualPaymentState = useCallback(
    (token: string): VirtualPaymentState =>
      submittingToken === token ? 'submitting' : createdTokens.has(token) ? 'created' : 'idle',
    [submittingToken, createdTokens]
  )

  const cancel = useCallback(
    async (ticketToken?: string) => {
      if (!enabled || !ticketToken) return
      try {
        await declineMatchingPayoutTicket(ticketToken)
        onDone()
      } catch (error) {
        console.error('Ошибка при отмене тикета:', error)
      } finally {
        setCancelToken(undefined)
      }
    },
    [enabled, onDone]
  )

  const modals = (
    <>
      <CompleteTicketModal
        show={!!completeToken}
        paused={confirmPaused}
        submitting={!!submittingToken}
        onSubmit={() => complete(completeToken)}
        onClose={() => {
          if (!submittingToken) setCompleteToken(undefined)
        }}
      />
      <CancelTicketModal
        show={!!cancelToken}
        paused={confirmPaused}
        onSubmit={() => cancel(cancelToken)}
        onClose={() => setCancelToken(undefined)}
      />
    </>
  )

  return {
    openComplete,
    openCancel,
    virtualPaymentState,
    modals,
  }
}

interface ModalProps {
  show: boolean
  /** Выплата на паузе — действие разрешено, но сотрудник подтверждает его осознанно. */
  paused: boolean
  onSubmit(): void
  onClose(): void
}

const PausedWarning: React.FC = () => (
  <p className='text-warning fw-bold mb-0'>Выплата на паузе. Точно выполнить?</p>
)

const CompleteTicketModal: React.FC<ModalProps & { submitting: boolean }> = ({
  show,
  paused,
  submitting,
  onSubmit,
  onClose,
}) => (
  <Modal show={show} onHide={onClose} centered backdrop={submitting ? 'static' : true}>
    <Modal.Header closeButton={!submitting}>
      <Modal.Title>Завершающая операция</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Этим действием будет создана выплата от виртуального мерчанта и пополнение от виртуального мерчанта.
      </p>
      <p>
        Выплата будет произведена на реквизиты выбранного тикета. А пополнение завершит мэтчинг тикет на указанную сумму.
      </p>
      {paused && <PausedWarning />}
    </Modal.Body>
    <Modal.Footer>
      <Button variant='secondary' onClick={onClose} disabled={submitting}>
        Отменить
      </Button>
      <Button variant='primary' onClick={onSubmit} disabled={submitting}>
        {submitting ? (
          <>
            <Spinner as='span' animation='border' size='sm' className='me-2' />
            Создаём платёж…
          </>
        ) : (
          'Подтвердить'
        )}
      </Button>
    </Modal.Footer>
  </Modal>
)

const CancelTicketModal: React.FC<ModalProps> = ({ show, paused, onSubmit, onClose }) => (
  <Modal show={show} onHide={onClose} centered>
    <Modal.Header closeButton>
      <Modal.Title>Подтверждение отмены тикета</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        Вы собираетесь <strong>отменить тикет</strong>. Это действие удалит тикет без создания
        нового.
      </p>
      {paused && <PausedWarning />}
    </Modal.Body>
    <Modal.Footer>
      <Button variant='secondary' onClick={onClose}>
        Отменить
      </Button>
      <Button variant='danger' onClick={onSubmit}>
        Подтвердить
      </Button>
    </Modal.Footer>
  </Modal>
)
